/**
 * JxChangeDiagnosticsPage — System · jXchange Diagnostics.
 *
 * Live up/down monitoring for the Jack Henry jXchange integration. Calls
 * POST /v3/admin/jxchange/probe (see AdminJxChangeProbeFunction.cs) which
 * runs four probes in parallel from the production Function App's outbound
 * IPs:
 *
 *   1. tcp-token       — TCP connect to ims.jhacloudservices.com:443
 *   2. tcp-rest-proxy  — TCP connect to jx-rl-proxy-jxapp.prod.ipaas.jha-sys.com:443
 *   3. oauth           — Forced OAuth token fetch (catches auth failures)
 *   4. rest-call       — ParmValSrch with Count=1 (catches proxy-up-but-broken)
 *
 * UI:
 *   - One row per probe with status badge, latency, last-success timestamp.
 *   - "Run Now" button for one-shot.
 *   - Active-Monitor toggle that polls at 15s / 30s / 1m / 5m intervals.
 *   - Browser-driven setInterval; pauses on document.hidden; cleans up on
 *     unmount.
 *   - Session-scoped history (last 50 results) renders a small timeline of
 *     overall-status flips so operators can see when something went down
 *     and when it recovered.
 *
 * Motivated by the 2026-05-18 shared-plan IP-rotation incident
 * (BACKLOG-0058). Built so the NEXT time JHA connectivity goes sideways,
 * operators have a single click instead of ad-hoc tcpping scripts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { SelectField, BoolField } from '@/components/FormField';

interface ProbeResult {
  name: string;
  label: string;
  status: 'ok' | 'fail';
  durationMs: number;
  detail: string;
}

interface ProbeResponse {
  success: boolean;
  probedAt: string;
  totalDurationMs: number;
  overallStatus: 'ok' | 'degraded';
  probes: ReadonlyArray<ProbeResult>;
  error?: string;
}

interface HistoryEntry {
  probedAt: string;
  overallStatus: 'ok' | 'degraded';
  totalDurationMs: number;
  probes: ReadonlyArray<ProbeResult>;
}

const INTERVAL_OPTIONS: ReadonlyArray<{ value: string; label: string; ms: number }> = [
  { value: '15', label: 'every 15 seconds', ms: 15_000 },
  { value: '30', label: 'every 30 seconds', ms: 30_000 },
  { value: '60', label: 'every minute', ms: 60_000 },
  { value: '300', label: 'every 5 minutes', ms: 300_000 },
];

const MAX_HISTORY = 50;

function statusPill(status: 'ok' | 'fail' | 'degraded'): React.ReactNode {
  if (status === 'ok') return <Pill tone="ok">OK</Pill>;
  if (status === 'degraded') return <Pill tone="warn">Degraded</Pill>;
  return <Pill tone="err">Fail</Pill>;
}

function fmtTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour12: false });
  } catch {
    return iso;
  }
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  if (diffMs < 5_000) return 'just now';
  if (diffMs < 60_000) return `${Math.floor(diffMs / 1000)}s ago`;
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  return `${Math.floor(diffMs / 3_600_000)}h ago`;
}

export function JxChangeDiagnosticsPage(): JSX.Element {
  const client = useApiClient();
  const [latest, setLatest] = useState<ProbeResponse | null>(null);
  const [history, setHistory] = useState<ReadonlyArray<HistoryEntry>>([]);
  const [lastSuccessByProbe, setLastSuccessByProbe] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [intervalKey, setIntervalKey] = useState<string>('60');
  const [flipBanner, setFlipBanner] = useState<{ tone: 'info' | 'warn' | 'err'; text: string } | null>(null);

  // Use a ref so the interval callback always sees the latest probe-runner.
  const runProbeRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // ── One probe run ──────────────────────────────────────────────────────
  const runProbe = useCallback(async (): Promise<void> => {
    if (running) return;
    setRunning(true);
    setError(null);
    try {
      const resp = (await client.retail.admin.jxchange.probe()) as ProbeResponse;
      setLatest(resp);

      // Update history (newest-first) with cap.
      setHistory((h) => {
        const next: HistoryEntry = {
          probedAt: resp.probedAt,
          overallStatus: resp.overallStatus,
          totalDurationMs: resp.totalDurationMs,
          probes: resp.probes,
        };
        const merged = [next, ...h];
        return merged.length > MAX_HISTORY ? merged.slice(0, MAX_HISTORY) : merged;
      });

      // Update last-success-by-probe.
      setLastSuccessByProbe((prev) => {
        const next: Record<string, string> = { ...prev };
        for (const p of resp.probes) {
          if (p.status === 'ok') next[p.name] = resp.probedAt;
        }
        return next;
      });

      // Banner on state-flip (compare against previous overall).
      setLatest((prevLatest) => {
        // Note: setLatest receives the latest functional value but we already
        // set it above with `setLatest(resp)`. The flip banner logic reads
        // the previous in the closure below by capturing `latest` outside
        // this state-update callback. To keep correctness, set the flip
        // banner from outside this updater (see below) — we just return resp.
        return resp;
      });
    } catch (e) {
      const msg = ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setRunning(false);
    }
  }, [client, running]);

  // Keep the ref pointed at the latest runProbe closure.
  useEffect(() => {
    runProbeRef.current = runProbe;
  }, [runProbe]);

  // ── State-flip banner detection ────────────────────────────────────────
  // Compare each newly arrived `latest` against the previous head of history
  // to emit a banner on transitions. Runs after `latest` changes.
  const prevOverallRef = useRef<'ok' | 'degraded' | null>(null);
  useEffect(() => {
    if (!latest) return;
    const prev = prevOverallRef.current;
    if (prev !== null && prev !== latest.overallStatus) {
      if (latest.overallStatus === 'ok') {
        setFlipBanner({ tone: 'info', text: `JHA recovered at ${fmtTimestamp(latest.probedAt)}. All probes passing.` });
      } else {
        const failing = latest.probes.filter((p) => p.status === 'fail').map((p) => p.name).join(', ');
        setFlipBanner({ tone: 'err', text: `JHA degraded at ${fmtTimestamp(latest.probedAt)}. Failing: ${failing}.` });
      }
    }
    prevOverallRef.current = latest.overallStatus;
  }, [latest]);

  // ── Initial probe on mount ─────────────────────────────────────────────
  useEffect(() => {
    void runProbeRef.current();
    // Run-once on mount only. Subsequent runs come from manual Run Now or
    // the active-monitor interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Active monitor (browser-driven polling) ────────────────────────────
  useEffect(() => {
    if (!active) return;
    const opt = INTERVAL_OPTIONS.find((o) => o.value === intervalKey) ?? INTERVAL_OPTIONS[2];
    const id = window.setInterval(() => {
      // Skip polling when tab is hidden — avoids burning the endpoint for
      // an unattended browser. Resumes on next interval when tab returns.
      if (document.hidden) return;
      void runProbeRef.current();
    }, opt.ms);
    return () => window.clearInterval(id);
  }, [active, intervalKey]);

  // ── Derived render data ────────────────────────────────────────────────
  const probes = latest?.probes ?? [];
  const overallStatus = latest?.overallStatus ?? null;

  const timeline = useMemo(() => {
    // Walk newest-first, collapse consecutive same-status entries into
    // segments so the timeline shows transitions, not every poll.
    const segs: Array<{ status: 'ok' | 'degraded'; from: string; to: string; count: number }> = [];
    for (let i = 0; i < history.length; i++) {
      const h = history[i]!;
      const prev = segs[segs.length - 1];
      if (prev && prev.status === h.overallStatus) {
        prev.from = h.probedAt;
        prev.count++;
      } else {
        segs.push({ status: h.overallStatus, from: h.probedAt, to: h.probedAt, count: 1 });
      }
    }
    return segs;
  }, [history]);

  return (
    <>
      {flipBanner && (
        <Banner tone={flipBanner.tone}>
          {flipBanner.text}{' '}
          <Button onClick={() => setFlipBanner(null)}>Dismiss</Button>
        </Banner>
      )}

      {error && (
        <Banner tone="err">
          Probe call failed: {error}{' '}
          <Button onClick={() => setError(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title="jXchange Diagnostics"
        actions={
          <div className="card-actions-row" style={{ gap: 12, alignItems: 'center' }}>
            <Button variant="primary" onClick={() => void runProbeRef.current()} disabled={running}>
              {running ? 'Running…' : 'Run Now'}
            </Button>
            <div style={{ minWidth: 200 }}>
              <SelectField
                label=""
                value={intervalKey}
                onChange={setIntervalKey}
                options={INTERVAL_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                disabled={!active}
              />
            </div>
            <BoolField
              label="Active monitor"
              value={active}
              onChange={setActive}
              hint=""
            />
          </div>
        }
      >
        <p className="empty" style={{ margin: '0 0 12px 0' }}>
          Probes run from inside the production Function App, so the source IPs
          match real customer traffic. Active monitor polls automatically and
          surfaces a banner on every state flip — leave it on during incident
          investigations to see recovery the moment it lands.
          {overallStatus && (
            <>
              {' '}Current overall status: <strong>{statusPill(overallStatus)}</strong>.
            </>
          )}
        </p>

        {!latest && running && <Spinner label="Running first probe…" />}

        {latest && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Probe</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 110 }}>Latency</th>
                <th style={{ width: 160 }}>Last success</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {probes.map((p) => (
                <tr key={p.name}>
                  <td><code className="cell-mono--bold">{p.label}</code></td>
                  <td>{statusPill(p.status)}</td>
                  <td>{p.durationMs} ms</td>
                  <td><span className="cell-dim">{fmtRelative(lastSuccessByProbe[p.name] ?? null)}</span></td>
                  <td><span className="cell-mono">{p.detail}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {latest && (
          <p className="empty" style={{ margin: '12px 0 0 0', fontSize: 12 }}>
            Probed {fmtTimestamp(latest.probedAt)} · total {latest.totalDurationMs} ms ·
            session history: {history.length} run{history.length === 1 ? '' : 's'} (max {MAX_HISTORY})
          </p>
        )}
      </Card>

      {timeline.length > 0 && (
        <Card title="Session timeline (state transitions)">
          <p className="empty" style={{ margin: '0 0 12px 0', fontSize: 13 }}>
            Newest first. Each segment collapses consecutive same-status polls into a single line.
          </p>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 200 }}>Latest probe in segment</th>
                <th>Run count in segment</th>
              </tr>
            </thead>
            <tbody>
              {timeline.map((seg, i) => (
                <tr key={`${seg.from}-${i}`}>
                  <td>{statusPill(seg.status)}</td>
                  <td><span className="cell-mono">{fmtTimestamp(seg.from)}</span></td>
                  <td>{seg.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
